/**
 * The sync engine: keeps the device and the server in step, in that order - first it SENDS what
 * the user did on this device (the outbox), then it PULLS what the server has.
 *
 * Push: the outbox's operations go up in order, in batches; the server answers each one (applied,
 * already applied, conflict, rejected) and the answers are recorded atomically (outbox.ts).
 * Pull: a page of changes at a time, each applied atomically together with the new cursor
 * (repo.applyPage), so being interrupted anywhere just resumes from the last committed page.
 * The server is the source of truth: whatever it says is what the device ends up holding.
 */
import { ApiError } from "@/lib/api";
import {
  applyResults, releaseBatch, resetInterrupted, takeBatch, toWire, type PushResponse, type WireOp,
} from "./outbox";
import { applyPage, getMeta, readCursor, setMeta, type PullPage } from "./repo";
import type { SqlDriver } from "./types";

export type SyncStatus = "idle" | "syncing" | "offline" | "error";

export interface SyncState {
  status: SyncStatus;
  /** True once the whole account has been downloaded at least once. Until then the app waits. */
  initialSyncDone: boolean;
  lastSyncAt: string | null;
  error: string | null;
  /** Change rows applied during the current sync (drives the first-run progress message). */
  applied: number;
}

const INITIAL: SyncState = { status: "idle", initialSyncDone: false, lastSyncAt: null, error: null, applied: 0 };
const PAGE_SIZE = 500;
const PUSH_BATCH = 100; // the server accepts at most 100 operations per request

export interface SyncHooks {
  fetchPage: (cursor: number, limit: number) => Promise<PullPage>;
  /** Sends queued operations. Without it the engine only pulls. */
  pushOps?: (ops: WireOp[]) => Promise<PushResponse>;
  /** Called after each page or push that changed local data (screens re-read their lists). */
  onData?: () => void;
  /** Called when a whole sync finishes; `changed` says whether it brought anything new. */
  onDone?: (changed: boolean) => void;
  /** The server said this session is no longer valid. */
  onUnauthorized?: (error: ApiError) => void;
}

export class SyncEngine {
  private state: SyncState = INITIAL;
  private readonly listeners = new Set<() => void>();
  private running: Promise<void> | null = null;
  private again = false;

  constructor(
    private readonly driver: SqlDriver,
    private readonly hooks: SyncHooks,
  ) {}

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): SyncState => this.state;

  private set(patch: Partial<SyncState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  async init(): Promise<void> {
    // A request that was in flight when the app closed is sent again; the server recognises it.
    await resetInterrupted(this.driver);
    this.set({
      initialSyncDone: (await getMeta(this.driver, "initial_sync_done")) === "1",
      lastSyncAt: await getMeta(this.driver, "last_sync_at"),
    });
  }

  /**
   * Sync now (push, then pull). Calls while one is running coalesce into a single follow-up run,
   * so a write made mid-sync is never missed but a burst of triggers doesn't stack up requests.
   */
  sync(): Promise<void> {
    if (this.running) {
      this.again = true;
      return this.running;
    }
    this.running = (async () => {
      try {
        do {
          this.again = false;
          await this.runOnce();
        } while (this.again);
      } finally {
        this.running = null;
      }
    })();
    return this.running;
  }

  /** Sends every queued operation. Returns whether anything was sent. */
  private async pushAll(): Promise<boolean> {
    const push = this.hooks.pushOps;
    if (!push) return false;
    let sent = false;
    for (;;) {
      const batch = await takeBatch(this.driver, PUSH_BATCH);
      if (batch.length === 0) return sent;
      try {
        await applyResults(this.driver, batch, await push(batch.map(toWire)));
      } catch (error) {
        // No answer (offline, timeout, 5xx): they go back in the queue, unchanged. If the server
        // did apply them, the next attempt is answered "already applied".
        await releaseBatch(this.driver, batch);
        throw error;
      }
      sent = true;
      this.hooks.onData?.();
    }
  }

  private async runOnce(): Promise<void> {
    this.set({ status: "syncing", error: null, applied: 0 });
    try {
      let changed = await this.pushAll();
      let cursor = await readCursor(this.driver);
      for (;;) {
        const page = await this.hooks.fetchPage(cursor, PAGE_SIZE);
        if (page.changes.length > 0 || page.next_cursor !== cursor) {
          await applyPage(this.driver, page);
          if (page.changes.length > 0) {
            changed = true;
            this.set({ applied: this.state.applied + page.changes.length });
            this.hooks.onData?.();
          }
        }
        cursor = page.next_cursor;
        if (!page.has_more) break;
      }
      const now = new Date().toISOString();
      await this.driver.batch([
        setMeta("initial_sync_done", "1"),
        setMeta("last_sync_at", now),
        // Applied operations the cursor has now passed no longer count towards the balance.
        { sql: `DELETE FROM outbox WHERE status = 'acked' AND acked_head <= ?`, params: [cursor] },
      ]);
      this.set({ status: "idle", initialSyncDone: true, lastSyncAt: now });
      this.hooks.onDone?.(changed);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        this.hooks.onUnauthorized?.(error);
        this.set({ status: "error", error: error.message });
      } else if (error instanceof ApiError && (error.status === 0 || error.code === "NETWORK_ERROR")) {
        this.set({ status: "offline", error: null });
      } else {
        this.set({ status: "error", error: error instanceof Error ? error.message : String(error) });
      }
    }
  }
}

/**
 * When to sync: on start, when the network returns, when the app comes back to the foreground,
 * and every minute while it is open and visible. (iOS has no background sync; syncing whenever
 * the app is in front of the user is the reliable substitute.)
 */
export function startAutoSync(engine: SyncEngine, intervalMs = 60_000): () => void {
  const trigger = () => {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
    void engine.sync();
  };
  const onOnline = () => void engine.sync();
  window.addEventListener("online", onOnline);
  document.addEventListener("visibilitychange", trigger);
  const timer = window.setInterval(trigger, intervalMs);
  void engine.sync();
  return () => {
    window.removeEventListener("online", onOnline);
    document.removeEventListener("visibilitychange", trigger);
    window.clearInterval(timer);
  };
}

// The one engine for the signed-in user, so code outside React (a mutation's onSuccess) can ask
// for a sync without prop-drilling.
let active: SyncEngine | null = null;
export const setActiveEngine = (engine: SyncEngine | null): void => {
  active = engine;
};
export const requestSync = (): Promise<void> => active?.sync() ?? Promise.resolve();
